import React, { createContext, useState, useContext, useEffect } from 'react';
import { trackEvent } from '../utils/analytics';
import favoritesTracker from '../utils/favoritesTracker';

const FavoritesContext = createContext();

export const useFavorites = () => useContext(FavoritesContext);

export const FavoritesProvider = ({ children }) => {
  const [favoriteModels, setFavoriteModels] = useState(() => {
    const saved = localStorage.getItem('favoriteModels');
    return saved ? JSON.parse(saved) : {};
  });
  
  const [favoritePhotoshoots, setFavoritePhotoshoots] = useState(() => {
    const saved = localStorage.getItem('favoritePhotoshoots');
    return saved ? JSON.parse(saved) : {};
  });

  // Save favorites to localStorage when they change
  useEffect(() => {
    localStorage.setItem('favoriteModels', JSON.stringify(favoriteModels));
  }, [favoriteModels]);

  useEffect(() => {
    localStorage.setItem('favoritePhotoshoots', JSON.stringify(favoritePhotoshoots));
  }, [favoritePhotoshoots]);

  // Toggle model favorite status
  const toggleModelFavorite = (modelId, modelName) => {
    setFavoriteModels(prev => {
      const newFavorites = { ...prev };
      const isRemoving = newFavorites[modelId];
      
      if (isRemoving) {
        delete newFavorites[modelId];
        trackEvent('Favorites', 'unfavorite_model', modelName);
        // Track global unfavorite
        favoritesTracker.trackFavorite('model', modelId, 'remove');
      } else {
        newFavorites[modelId] = true;
        trackEvent('Favorites', 'favorite_model', modelName);
        // Track global favorite
        favoritesTracker.trackFavorite('model', modelId, 'add');
      }
      return newFavorites;
    });
  };

  // Toggle photoshoot favorite status
  const togglePhotoshootFavorite = (photoshootId, photoshootTitle) => {
    setFavoritePhotoshoots(prev => {
      const newFavorites = { ...prev };
      const isRemoving = newFavorites[photoshootId];
      
      if (isRemoving) {
        delete newFavorites[photoshootId];
        trackEvent('Favorites', 'unfavorite_photoshoot', photoshootTitle);
        // Track global unfavorite
        favoritesTracker.trackFavorite('photoshoot', photoshootId, 'remove');
      } else {
        newFavorites[photoshootId] = true;
        trackEvent('Favorites', 'favorite_photoshoot', photoshootTitle);
        // Track global favorite
        favoritesTracker.trackFavorite('photoshoot', photoshootId, 'add');
      }
      return newFavorites;
    });
  };

  // Check if a model is favorited
  const isModelFavorited = (modelId) => {
    return !!favoriteModels[modelId];
  };

  // Check if a photoshoot is favorited
  const isPhotoshootFavorited = (photoshootId) => {
    return !!favoritePhotoshoots[photoshootId];
  };

  // Get count of favorited models
  const getFavoriteModelsCount = () => {
    return Object.keys(favoriteModels).length;
  };

  // Get count of favorited photoshoots
  const getFavoritePhotoshootsCount = () => {
    return Object.keys(favoritePhotoshoots).length;
  };

  // Get all favorited models IDs
  const getFavoriteModelIds = () => {
    return Object.keys(favoriteModels).map(id => parseInt(id));
  };

  // Get all favorited photoshoot IDs
  const getFavoritePhotoshootIds = () => {
    return Object.keys(favoritePhotoshoots).map(id => parseInt(id));
  };

  return (
    <FavoritesContext.Provider value={{
      toggleModelFavorite,
      togglePhotoshootFavorite,
      isModelFavorited,
      isPhotoshootFavorited,
      getFavoriteModelsCount,
      getFavoritePhotoshootsCount,
      getFavoriteModelIds,
      getFavoritePhotoshootIds
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};